import { memo, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Avatar, Badge, Box, Button, Card, Group, Stack, Text } from 'components'
import { useArcTranslation } from 'hooks'
import { getInitials } from 'helpers'
import { getPlaceholderImage } from 'pages/ArcProjectsList/utils'
import { STATUS_DISPLAY_MAP } from 'pages/ArcProjectsList/types'
import type { ProjectMiniCardProps } from '../interfaces'
import { DEFAULT_STATUS_CONFIG, PROJECT_COVER_HEIGHT } from '../constants'
import { buildProjectUrl } from '../helpers'

const cardStyle = { display: 'flex', flexDirection: 'column' as const, height: '100%' }

/** Compact project card for the dashboard: cover, status, grade and author. */
export const ProjectMiniCard = memo(({ project }: ProjectMiniCardProps) => {
  const { t } = useArcTranslation()

  const statusConfig = STATUS_DISPLAY_MAP[project.status] ?? DEFAULT_STATUS_CONFIG
  const authorName = project.authorName ?? '—'

  const gradesLabel = useMemo(
    () =>
      project.projectGrades
        ?.map(projectGrade => projectGrade?.grade?.name)
        .filter(Boolean)
        .join(', ') ?? '',
    [project.projectGrades],
  )

  return (
    <Card shadow='sm' withBorder radius='md' p={0} style={cardStyle}>
      <Box
        aria-hidden
        sx={{
          height: PROJECT_COVER_HEIGHT,
          position: 'relative',
          backgroundImage: `url(${getPlaceholderImage(project.id)})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderTopLeftRadius: 'var(--mantine-radius-md)',
          borderTopRightRadius: 'var(--mantine-radius-md)',
        }}
      >
        <Badge color={statusConfig.color} variant='light' sx={{ position: 'absolute', bottom: 8, left: 8 }}>
          {t(statusConfig.labelKey)}
        </Badge>
      </Box>

      <Stack spacing={6} p='md' style={{ flex: 1 }}>
        <Text weight={600} lineClamp={2}>
          {project.name}
        </Text>

        {!!gradesLabel && (
          <Text size='xs' color='dimmed'>
            {t('arc_projects.grade_label')}: {gradesLabel}
          </Text>
        )}

        <Group spacing={8} mt='auto' noWrap>
          <Avatar size='sm' radius='xl' alt={authorName}>
            {getInitials(authorName)}
          </Avatar>
          <Box>
            <Text size='xs' color='dimmed' tt='uppercase'>
              {t('manager_dashboard.created_by')}
            </Text>
            <Text size='sm'>{authorName}</Text>
          </Box>
        </Group>
      </Stack>

      <Box px='md' pb='md'>
        <Button component={Link} to={buildProjectUrl(project.id)} variant='outline' radius='xl' size='sm' fullWidth>
          {t('manager_dashboard.view_project')}
        </Button>
      </Box>
    </Card>
  )
})

ProjectMiniCard.displayName = 'ProjectMiniCard'
